import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type {
  PfLookupItem,
  PublicFormLookupResult,
  ResolvedFormCatalogField,
} from '../../forms/patientFill/types';
import { isStoredMediaKey, resolveFormAssetUrl, resolveSignatureDisplaySrc } from '../../forms/patientFill/formPayload';
import { usePatientFormMedia } from './PatientFormMediaContext';
import { DEFAULT_GOOGLE_MAPS_API_KEY } from '../../growth-local-market/config';

/** Shared field surface — the interactive twin of `.fm-field-preview`. */
export const PF_FIELD_CLASS =
  'w-full rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[13px] py-[11px] text-[13.5px] text-[var(--ink-0)] outline-none transition-[border-color,box-shadow,background] placeholder:text-[var(--ink-4)] focus:border-[var(--ai-3)] focus:shadow-[0_0_0_3px_var(--ai-tint)] disabled:cursor-not-allowed disabled:opacity-60';

export type FieldControlType =
  | 'text'
  | 'email'
  | 'tel'
  | 'number'
  | 'currency'
  | 'ssn'
  | 'ssn_full'
  | 'textarea'
  | 'date'
  | 'dropdown'
  | 'radio'
  | 'checkbox'
  | 'checkbox_group'
  | 'tags'
  | 'lookup'
  | 'signature'
  | 'file'
  | 'dynamic_list'
  | 'address'
  | 'display';

/** Shared lookup-search signature: results carry the canonical id + code (FD142). */
export type LookupSearchFn = (query: string) => Promise<PublicFormLookupResult[]>;

export interface FieldControlProps {
  type: FieldControlType;
  value: unknown;
  onChange: (value: unknown) => void;
  blockId?: string;
  fieldId?: string;
  placeholder?: string;
  options?: string[];
  disabled?: boolean;
  error?: boolean;
  content?: string;
  locked?: boolean;
  onLookupSearch?: LookupSearchFn;
  /** Radio / checkbox group: append an "Other" choice with a free-text box. */
  allowOther?: boolean;
  /** Radio / checkbox group: lay options out in N columns from `sm` up. */
  columns?: number;
  /** File: `accept` attribute, e.g. `.png,.jpg,.jpeg`. */
  accept?: string;
}

const OTHER_OPTION_LABEL = 'Other';
const OTHER_PREFIX = `${OTHER_OPTION_LABEL}: `;

function isOtherAnswer(answer: string): boolean {
  return answer === OTHER_OPTION_LABEL || answer.startsWith(OTHER_PREFIX);
}

function otherText(answer: string | undefined): string {
  return answer?.startsWith(OTHER_PREFIX) ? answer.slice(OTHER_PREFIX.length) : '';
}

/** "Other" answers stay a plain string (`Other: …`) so `contains "Other"` conditions still match. */
function toOtherAnswer(text: string): string {
  return text.trim() ? `${OTHER_PREFIX}${text}` : OTHER_OPTION_LABEL;
}

const OPTION_COLUMNS_CLASS: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
};

function optionListClass(columns: number | undefined, fallback: string): string {
  const cols = columns ? OPTION_COLUMNS_CLASS[columns] : undefined;
  return cols ? cn('grid grid-cols-1 gap-x-5 gap-y-2.5 pt-1', cols) : fallback;
}

function formatFullSsn(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function OtherTextInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean | undefined;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      placeholder="Please specify"
      disabled={disabled}
      className={cn(PF_FIELD_CLASS, 'mt-2')}
    />
  );
}

export function FieldLabel({
  label,
  required,
  locked,
  badge,
  hint,
}: {
  label: string;
  required?: boolean;
  locked?: boolean;
  badge?: { label: string; color: string };
  hint?: string;
}) {
  return (
    <div className="mb-[7px] flex flex-wrap items-center gap-1.5">
      <span className="text-[13px] font-medium text-[var(--ink-1)]">
        {label}
        {required ? <span className="text-[var(--terracotta)]"> *</span> : null}
      </span>
      {locked ? <Icon kind="lock" size={11} className="text-[var(--ink-4)]" /> : null}
      {badge ? (
        <span
          className="rounded px-1.5 py-0.5 font-[var(--font-mono)] text-[9px] font-semibold uppercase tracking-wide"
          style={{
            background: `color-mix(in srgb, ${badge.color} 12%, var(--cream-1))`,
            color: badge.color,
          }}
        >
          {badge.label}
        </span>
      ) : null}
      {hint ? (
        <span className="ml-auto font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">{hint}</span>
      ) : null}
    </div>
  );
}

function asString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value === 'string' && value.trim()) return [value];
  return [];
}

export function FieldControl({
  type,
  value,
  onChange,
  blockId,
  fieldId,
  placeholder,
  options = [],
  disabled,
  error,
  content,
  locked,
  onLookupSearch,
  allowOther,
  columns,
  accept,
}: FieldControlProps) {
  switch (type) {
    case 'display':
      return (
        <div
          className={cn(
            'rounded-[var(--r-md)] px-3 py-2.5 text-[12.5px] leading-relaxed',
            locked
              ? 'border border-[color-mix(in_srgb,var(--gold)_25%,var(--cream-2))] bg-[var(--gold-tint)] text-[var(--ink-1)]'
              : 'bg-[var(--cream-0)] text-[var(--ink-1)]',
          )}
        >
          {locked ? (
            <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--gold)]">
              <Icon kind="lock" size={9} />
              Locked legal text
            </div>
          ) : null}
          <div className="whitespace-pre-wrap">{content || ''}</div>
        </div>
      );

    case 'textarea':
      return (
        <textarea
          value={asString(value)}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={placeholder ?? 'Type your answer…'}
          disabled={disabled}
          className={cn(PF_FIELD_CLASS, 'min-h-[96px] resize-y leading-relaxed', error && 'border-[var(--terracotta)]')}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={asString(value)}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={placeholder ?? '0'}
          disabled={disabled}
          className={cn(PF_FIELD_CLASS, error && 'border-[var(--terracotta)]')}
        />
      );

    case 'currency':
      return (
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13.5px] text-[var(--ink-3)]">
            $
          </span>
          <input
            type="number"
            inputMode="decimal"
            value={asString(value)}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder="0.00"
            disabled={disabled}
            className={cn(PF_FIELD_CLASS, 'pl-7', error && 'border-[var(--terracotta)]')}
          />
        </div>
      );

    case 'date':
      return (
        <input
          type="date"
          value={asString(value)}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          disabled={disabled}
          className={cn(PF_FIELD_CLASS, error && 'border-[var(--terracotta)]')}
        />
      );

    case 'ssn':
      return (
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          value={asString(value)}
          onChange={(e) => {
            onChange(e.target.value.replace(/\D/g, '').slice(0, 4));
          }}
          placeholder="••••"
          disabled={disabled}
          className={cn(PF_FIELD_CLASS, 'font-[var(--font-mono)]', error && 'border-[var(--terracotta)]')}
        />
      );

    case 'dropdown':
      return (
        <SelectControl
          value={asString(value)}
          onChange={onChange}
          options={options}
          placeholder={placeholder ?? 'Select…'}
          disabled={disabled}
          error={error}
        />
      );

    case 'ssn_full':
      return (
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={11}
          value={formatFullSsn(asString(value))}
          onChange={(e) => {
            onChange(formatFullSsn(e.target.value));
          }}
          placeholder="###-##-####"
          disabled={disabled}
          className={cn(PF_FIELD_CLASS, 'font-[var(--font-mono)]', error && 'border-[var(--terracotta)]')}
        />
      );

    case 'radio': {
      const current = asString(value);
      const baseOptions = (options.length ? options : ['Option A', 'Option B']).filter(
        (opt) => !(allowOther && opt === OTHER_OPTION_LABEL),
      );
      const otherOn = Boolean(allowOther && isOtherAnswer(current));
      const renderRadio = (key: string, label: string, on: boolean, onClick: () => void) => (
        <button
          key={key}
          type="button"
          disabled={disabled}
          onClick={onClick}
          className="inline-flex cursor-pointer items-center gap-2.5 text-[13.5px] text-[var(--ink-1)] disabled:cursor-not-allowed"
        >
          <span
            className={cn(
              'flex size-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] bg-[var(--cream-1)] transition',
              on ? 'border-[var(--ai-3)]' : 'border-[var(--cream-shadow)]',
            )}
          >
            {on ? <span className="size-[9px] rounded-full bg-[var(--ai-3)]" /> : null}
          </span>
          {label}
        </button>
      );
      return (
        <div>
          <div className={optionListClass(columns, 'flex flex-wrap gap-x-5 gap-y-2.5 pt-1')}>
            {baseOptions.map((opt) =>
              renderRadio(opt, opt, current === opt, () => {
                onChange(opt);
              }),
            )}
            {allowOther
              ? renderRadio('__other', OTHER_OPTION_LABEL, otherOn, () => {
                  onChange(toOtherAnswer(otherText(current)));
                })
              : null}
          </div>
          {otherOn ? (
            <OtherTextInput
              value={otherText(current)}
              onChange={(text) => {
                onChange(toOtherAnswer(text));
              }}
              disabled={disabled}
            />
          ) : null}
        </div>
      );
    }

    case 'checkbox': {
      const on = value === true || value === 'true';
      const label = options[0] ?? placeholder ?? '';
      return (
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            onChange(!on);
          }}
          className="inline-flex cursor-pointer items-start gap-2.5 text-left text-[13.5px] leading-snug text-[var(--ink-1)] disabled:cursor-not-allowed"
        >
          <span
            className={cn(
              'mt-px flex size-[18px] shrink-0 items-center justify-center rounded-[6px] border-[1.5px] transition',
              on ? 'border-[var(--ai-3)] bg-[var(--ai-3)] text-white' : 'border-[var(--cream-shadow)] bg-[var(--cream-1)]',
            )}
          >
            {on ? <Icon kind="check" size={12} stroke={3} /> : null}
          </span>
          <span>{label}</span>
        </button>
      );
    }

    case 'checkbox_group': {
      const selected = asStringArray(value);
      const baseOptions = (options.length ? options : ['Option 1', 'Option 2', 'Option 3']).filter(
        (opt) => !(allowOther && opt === OTHER_OPTION_LABEL),
      );
      const otherAnswer = allowOther ? selected.find(isOtherAnswer) : undefined;
      const renderBox = (key: string, label: string, on: boolean, onClick: () => void) => (
        <button
          key={key}
          type="button"
          disabled={disabled}
          onClick={onClick}
          className="inline-flex cursor-pointer items-center gap-2.5 text-left text-[13.5px] text-[var(--ink-1)] disabled:cursor-not-allowed"
        >
          <span
            className={cn(
              'flex size-[18px] shrink-0 items-center justify-center rounded-[6px] border-[1.5px] transition',
              on ? 'border-[var(--ai-3)] bg-[var(--ai-3)] text-white' : 'border-[var(--cream-shadow)] bg-[var(--cream-1)]',
            )}
          >
            {on ? <Icon kind="check" size={12} stroke={3} /> : null}
          </span>
          {label}
        </button>
      );
      return (
        <div>
          <div className={optionListClass(columns, 'flex flex-col gap-2 pt-1')}>
            {baseOptions.map((opt) => {
              const on = selected.includes(opt);
              return renderBox(opt, opt, on, () => {
                onChange(on ? selected.filter((s) => s !== opt) : [...selected, opt]);
              });
            })}
            {allowOther
              ? renderBox('__other', OTHER_OPTION_LABEL, otherAnswer !== undefined, () => {
                  onChange(
                    otherAnswer !== undefined
                      ? selected.filter((s) => s !== otherAnswer)
                      : [...selected, OTHER_OPTION_LABEL],
                  );
                })
              : null}
          </div>
          {otherAnswer !== undefined ? (
            <OtherTextInput
              value={otherText(otherAnswer)}
              onChange={(text) => {
                onChange(selected.map((s) => (s === otherAnswer ? toOtherAnswer(text) : s)));
              }}
              disabled={disabled}
            />
          ) : null}
        </div>
      );
    }

    case 'tags':
      return (
        <LookupControl
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          {...(onLookupSearch ? { onLookupSearch } : {})}
        />
      );

    case 'lookup':
      return (
        <LookupControl
          single
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          {...(onLookupSearch ? { onLookupSearch } : {})}
        />
      );

    case 'signature':
      return (
        <SignatureControl
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          {...(blockId ? { blockId } : {})}
          {...(fieldId ? { fieldId } : {})}
        />
      );

    case 'file':
      return (
        <FileControl
          value={value}
          onChange={onChange}
          disabled={disabled}
          {...(blockId ? { blockId } : {})}
          {...(accept ? { accept } : {})}
        />
      );

    case 'dynamic_list':
      return (
        <DynamicListControl
          value={value}
          onChange={onChange}
          disabled={disabled}
          {...(placeholder ? { placeholder } : {})}
        />
      );

    case 'address':
      return (
        <AddressControl
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          {...(placeholder ? { placeholder } : {})}
        />
      );

    case 'email':
    case 'tel':
    case 'text':
    default:
      return (
        <input
          type={type === 'email' ? 'email' : type === 'tel' ? 'tel' : 'text'}
          value={asString(value)}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={placeholder ?? (type === 'email' ? 'name@example.com' : type === 'tel' ? '(555) 123-4567' : 'Placeholder…')}
          disabled={disabled}
          className={cn(PF_FIELD_CLASS, error && 'border-[var(--terracotta)]')}
        />
      );
  }
}

function SelectControl({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean | undefined;
  error?: boolean | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className={cn(
          PF_FIELD_CLASS,
          'flex cursor-pointer items-center justify-between gap-2 text-left',
          error && 'border-[var(--terracotta)]',
        )}
      >
        <span className={value ? 'text-[var(--ink-0)]' : 'text-[var(--ink-4)]'}>{value || placeholder}</span>
        <Icon kind="down" size={14} className="shrink-0 text-[var(--ink-3)]" />
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-[260px] overflow-y-auto rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-1.5 shadow-[var(--sh-3)]">
          {options.map((opt) => {
            const active = opt === value;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full cursor-pointer items-center justify-between gap-2 rounded-[var(--r-sm)] px-3 py-[9px] text-left text-[13.5px] text-[var(--ink-1)] hover:bg-[var(--cream-0)]',
                  active && 'bg-[var(--cream-0)]',
                )}
              >
                {opt}
                {active ? <Icon kind="check" size={14} stroke={2.4} className="text-[var(--ai-3)]" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function pfIsRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Coerce a stored value into a wire item, tolerating legacy bare strings on read. */
function pfNormItem(v: unknown): PfLookupItem | null {
  if (pfIsRecord(v)) {
    if (v.other === true && typeof v.text === 'string' && v.text) {
      return { other: true, text: v.text };
    }
    if (typeof v.id === 'string' && v.id && typeof v.label === 'string') {
      return { id: v.id, label: v.label, code: typeof v.code === 'string' ? v.code : '' };
    }
    return null;
  }
  // Legacy bare string (pre-D36 data / mock) — displayed as "other", never
  // silently promoted to a catalog selection. New input goes through the choice.
  if (typeof v === 'string' && v.trim()) return { other: true, text: v.trim() };
  return null;
}

function pfNormItems(raw: unknown): PfLookupItem[] {
  if (Array.isArray(raw)) {
    return raw.map(pfNormItem).filter((item): item is PfLookupItem => item !== null);
  }
  const one = pfNormItem(raw);
  return one ? [one] : [];
}

function pfItemLabel(item: PfLookupItem): string {
  return 'other' in item ? item.text : item.label;
}

function pfItemKey(item: PfLookupItem): string {
  return 'other' in item ? `other:${item.text.toLowerCase()}` : `sel:${item.id}`;
}

/**
 * Structured lookup control (W9/W10 · FD142 · D36).
 *
 * Replaces the old free-typing tags input. The answer is a **choice**: search →
 * select a catalog match (stored as `{id,label,code}`), or "Not listed / Other"
 * → free text (stored as `{other:true,text}`). An unmatched typed string is
 * NEVER silently committed as a catalog value — that is the uncontrolled
 * behaviour D36 exists to end. `single` fields (a `search`, e.g. carrier) hold
 * one item and emit it directly; multi fields (`search_multi`) hold a list.
 */
function LookupControl({
  value,
  onChange,
  placeholder,
  disabled,
  onLookupSearch,
  single,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  onLookupSearch?: LookupSearchFn | undefined;
  single?: boolean | undefined;
}) {
  const items = pfNormItems(value);
  const [draft, setDraft] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<PublicFormLookupResult[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [otherMode, setOtherMode] = React.useState(false);
  const [otherText, setOtherText] = React.useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const emit = (next: PfLookupItem[]) => {
    onChange(single ? (next[0] ?? undefined) : next);
  };
  const addItem = (item: PfLookupItem) => {
    const key = pfItemKey(item);
    const next = single ? [item] : items.some((i) => pfItemKey(i) === key) ? items : [...items, item];
    emit(next);
    setDraft('');
    setSuggestions([]);
    setOtherMode(false);
    setOtherText('');
  };
  const removeAt = (idx: number) => {
    emit(items.filter((_, i) => i !== idx));
  };

  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  React.useEffect(() => {
    if (!onLookupSearch || otherMode) {
      setSuggestions([]);
      return;
    }
    const query = draft.trim();
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      void onLookupSearch(query)
        .then((results) => {
          setSuggestions(results);
        })
        .catch(() => {
          setSuggestions([]);
        })
        .finally(() => {
          setSearching(false);
        });
    }, 250);
  }, [draft, onLookupSearch, otherMode]);

  const inputHidden = single === true && items.length >= 1;
  const showDropdown = !disabled && !otherMode && !inputHidden && draft.trim().length >= 1;

  return (
    <div className="relative">
      <div className={cn(PF_FIELD_CLASS, 'flex flex-wrap items-center gap-1.5')}>
        {items.map((item, idx) => (
          <span
            key={pfItemKey(item)}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--ai-tint)] px-2 py-0.5 text-[11.5px] font-medium text-[var(--ai-3)]"
          >
            {pfItemLabel(item)}
            {'other' in item ? (
              <span className="text-[10px] font-normal text-[var(--ink-4)]">(not listed)</span>
            ) : null}
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                removeAt(idx);
              }}
              className="cursor-pointer opacity-70 hover:opacity-100"
            >
              <Icon kind="x" size={10} />
            </button>
          </span>
        ))}

        {otherMode ? (
          <span className="flex flex-1 items-center gap-1.5">
            <input
              autoFocus
              value={otherText}
              disabled={disabled}
              onChange={(e) => {
                setOtherText(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const text = otherText.trim();
                  if (text) addItem({ other: true, text });
                } else if (e.key === 'Escape') {
                  setOtherMode(false);
                  setOtherText('');
                }
              }}
              placeholder="Type what isn't listed…"
              className="min-w-[120px] flex-1 border-0 bg-transparent p-0 text-[13.5px] text-[var(--ink-0)] outline-none placeholder:text-[var(--ink-4)]"
            />
            <button
              type="button"
              disabled={disabled === true || otherText.trim().length === 0}
              onClick={() => {
                const text = otherText.trim();
                if (text) addItem({ other: true, text });
              }}
              className="cursor-pointer rounded-[var(--r-sm)] bg-[var(--ai-3)] px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50"
            >
              Add
            </button>
          </span>
        ) : inputHidden ? null : (
          <input
            value={draft}
            disabled={disabled}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !draft && items.length) {
                removeAt(items.length - 1);
              }
            }}
            placeholder={items.length ? '' : placeholder ?? 'Search to add…'}
            className="min-w-[120px] flex-1 border-0 bg-transparent p-0 text-[13.5px] text-[var(--ink-0)] outline-none placeholder:text-[var(--ink-4)]"
          />
        )}

        {searching ? <span className="text-[11px] text-[var(--ink-4)]">…</span> : null}
      </div>

      {showDropdown ? (
        <ul className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] py-1 shadow-[var(--sh-2)]">
          {suggestions.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="block w-full cursor-pointer px-3 py-2 text-left text-[13px] text-[var(--ink-1)] hover:bg-[var(--cream-0)]"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addItem({ id: item.id, label: item.label, code: item.code ?? '' });
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
          <li className="border-t border-[var(--cream-2)]">
            <button
              type="button"
              className="block w-full cursor-pointer px-3 py-2 text-left text-[13px] font-medium text-[var(--ai-3)] hover:bg-[var(--cream-0)]"
              onMouseDown={(e) => {
                e.preventDefault();
                setOtherText(draft.trim());
                setOtherMode(true);
              }}
            >
              Not listed / Other{draft.trim() ? ` — “${draft.trim()}”` : ''}
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  );
}

function StoredMediaPreview({
  fileKey,
  blockId,
  label,
  className,
}: {
  fileKey: string;
  blockId?: string;
  label?: string;
  className?: string;
}) {
  const media = usePatientFormMedia();
  const url = resolveFormAssetUrl(fileKey, media, blockId);
  const name = fileKey.split('/').pop() ?? fileKey;
  const isImage = /\.(jpe?g|png|gif|webp)$/i.test(fileKey);

  if (isImage) {
    return (
      <div className={cn('overflow-hidden rounded-[9px] border border-[var(--cream-2)] bg-white', className)}>
        <img src={url} alt={label ?? name} referrerPolicy="no-referrer" className="max-h-[220px] w-full object-contain" />
        <div className="border-t border-[var(--cream-2)] px-3 py-2 text-[11.5px] text-[var(--ink-3)]">
          <a href={url} target="_blank" rel="noopener noreferrer" className="font-medium text-[var(--ai-3)] hover:underline">
            {name}
          </a>
        </div>
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'inline-flex items-center gap-2 rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-2.5 text-[12.5px] font-medium text-[var(--ai-3)] hover:bg-[var(--ai-tint)]',
        className,
      )}
    >
      <Icon kind="upload" size={14} />
      {name || label || 'View uploaded file'}
    </a>
  );
}

function FileControl({
  value,
  onChange,
  disabled,
  blockId,
  accept,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean | undefined;
  blockId?: string;
  accept?: string;
}) {
  if (typeof value === 'string' && value && isStoredMediaKey(value)) {
    return <StoredMediaPreview fileKey={value} {...(blockId ? { blockId } : {})} />;
  }

  const name = value instanceof File ? value.name : '';
  return (
    <label
      className={cn(
        'block w-full cursor-pointer rounded-[9px] border-[1.5px] border-dashed border-[var(--cream-shadow)] bg-[var(--cream-0)] px-4 py-5 text-center text-[12.5px] text-[var(--ink-3)] transition hover:border-[var(--ai-3)] hover:bg-[var(--ai-tint)]',
        disabled && 'pointer-events-none opacity-60',
      )}
    >
      <input
        type="file"
        className="hidden"
        disabled={disabled}
        {...(accept ? { accept } : {})}
        onChange={(e) => {
          onChange(e.target.files?.[0] ?? null);
        }}
      />
      <Icon kind="upload" size={16} className="mx-auto mb-1.5 opacity-70" />
      <div>{name || 'Drag a file or tap to upload'}</div>
    </label>
  );
}

function SignatureControl({
  value,
  onChange,
  disabled,
  error,
  blockId,
  fieldId,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean | undefined;
  error?: boolean | undefined;
  blockId?: string;
  fieldId?: string;
}) {
  const media = usePatientFormMedia();
  const PAPER = '#ffffff';
  const INK = '#1f2430';

  const displayValue = resolveSignatureDisplaySrc(value, media, blockId, fieldId);
  const hasStoredImage = Boolean(displayValue);

  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const drawing = React.useRef(false);
  const hasInk = React.useRef(hasStoredImage);

  const fillPaper = React.useCallback((ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  React.useEffect(() => {
    hasInk.current = hasStoredImage;
  }, [hasStoredImage]);

  // Restore ink whenever the bound value changes (e.g. session loads after mount).
  React.useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    fillPaper(ctx, canvas);
    hasInk.current = false;
    if (!displayValue) return;

    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.onload = () => {
      fillPaper(ctx, canvas);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      hasInk.current = true;
    };
    img.onerror = () => {
      fillPaper(ctx, canvas);
      hasInk.current = false;
    };
    img.src = displayValue;
  }, [displayValue, fillPaper]);

  if (disabled && hasStoredImage) {
    return (
      <div
        className={cn(
          'flex h-[150px] w-full items-center justify-center overflow-hidden rounded-[9px] border-[1.5px] border-dashed bg-white',
          error ? 'border-[var(--terracotta)]' : 'border-[var(--cream-shadow)]',
        )}
      >
        <img src={displayValue} alt="Signature" referrerPolicy="no-referrer" className="max-h-full max-w-full object-contain" />
      </div>
    );
  }

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = INK;
    ctx.lineTo(x, y);
    ctx.stroke();
    hasInk.current = true;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (canvas && hasInk.current) onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) fillPaper(ctx, canvas);
    hasInk.current = false;
    onChange(null);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          'relative h-[150px] w-full overflow-hidden rounded-[9px] border-[1.5px] border-dashed bg-white',
          error ? 'border-[var(--terracotta)]' : 'border-[var(--cream-shadow)]',
        )}
      >
        <canvas
          ref={canvasRef}
          width={760}
          height={150}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          className="absolute inset-0 h-full w-full touch-none"
          style={{ cursor: disabled ? 'not-allowed' : 'crosshair' }}
        />
        {!displayValue && !hasInk.current ? (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center gap-1.5 text-[12.5px] italic text-[#9aa0aa]">
            <Icon kind="pencil" size={12} />
            Sign with your finger or mouse
          </span>
        ) : null}
        <div className="pointer-events-none absolute inset-x-4 bottom-6 border-b border-dashed border-[#c9ced8]" />
      </div>
      <button
        type="button"
        onClick={clear}
        disabled={disabled}
        className="inline-flex w-fit cursor-pointer items-center gap-1.5 text-[11.5px] font-medium text-[var(--ink-3)] hover:text-[var(--ink-1)] disabled:cursor-not-allowed"
      >
        <Icon kind="trash" size={11} />
        Clear signature
      </button>
    </div>
  );
}

/**
 * W11 — `dynamic_list` renderer. A repeating list of text entries (e.g. the
 * additional parties present during a recording). Before this it fell through to
 * a single-line text box, breaking the field. Value on the wire is `string[]`.
 */
function DynamicListControl({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
}) {
  const stored = asStringArray(value);
  const rows = stored.length > 0 ? stored : [''];

  const emit = (next: string[]) => {
    onChange(next.every((row) => row.trim().length === 0) ? [] : next);
  };
  const updateRow = (index: number, next: string) => {
    emit(rows.map((row, i) => (i === index ? next : row)));
  };
  const addRow = () => {
    onChange([...rows, '']);
  };
  const removeRow = (index: number) => {
    emit(rows.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-1.5">
          <input
            value={row}
            onChange={(e) => {
              updateRow(index, e.target.value);
            }}
            placeholder={placeholder ?? `Entry ${String(index + 1)}`}
            disabled={disabled}
            className={PF_FIELD_CLASS}
          />
          <button
            type="button"
            title="Remove"
            disabled={disabled === true || rows.length <= 1}
            onClick={() => {
              removeRow(index);
            }}
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-3)] transition-colors hover:border-[var(--terracotta)] hover:text-[var(--terracotta)]',
              (disabled === true || rows.length <= 1) && 'cursor-not-allowed opacity-40',
            )}
          >
            <Icon kind="trash" size={13} />
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={disabled}
        onClick={addRow}
        className="inline-flex w-fit items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--ink-2)] transition-colors hover:border-[var(--ai-3)] hover:text-[var(--ai-3)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Icon kind="plus" size={12} />
        Add another
      </button>
    </div>
  );
}

/**
 * W12 — `google` / `patient_address` address autocomplete. Uses Google Places if
 * it loads (reusing the shared Maps key); the emitted value stays a plain address
 * string so the wire contract is unchanged. If Places can't load (offline / no
 * key), it degrades to exactly the plain text input it was before — never breaks.
 */
interface PlacesAutocompleteInstance {
  addListener: (event: string, handler: () => void) => void;
  getPlace: () => { formatted_address?: string };
}
interface PlacesLibrary {
  Autocomplete: new (
    input: HTMLInputElement,
    opts?: { types?: string[]; fields?: string[] },
  ) => PlacesAutocompleteInstance;
}
interface MapsGlobal {
  google?: { maps?: { places?: PlacesLibrary } };
  __elvaPlacesInit?: () => void;
}

const PLACES_SCRIPT_ID = 'elva-google-places';
let placesLoaderPromise: Promise<PlacesLibrary | null> | null = null;

function loadGooglePlaces(): Promise<PlacesLibrary | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve(null);
  const w = window as unknown as MapsGlobal;
  const existingLib = w.google?.maps?.places;
  if (existingLib) return Promise.resolve(existingLib);
  if (placesLoaderPromise) return placesLoaderPromise;

  placesLoaderPromise = new Promise<PlacesLibrary | null>((resolve) => {
    const finish = () => {
      resolve((window as unknown as MapsGlobal).google?.maps?.places ?? null);
    };
    const existingScript = document.getElementById(PLACES_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', finish);
      existingScript.addEventListener('error', () => {
        resolve(null);
      });
      return;
    }
    w.__elvaPlacesInit = finish;
    const script = document.createElement('script');
    script.id = PLACES_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${DEFAULT_GOOGLE_MAPS_API_KEY}` +
      '&libraries=places&loading=async&callback=__elvaPlacesInit';
    script.addEventListener('error', () => {
      placesLoaderPromise = null;
      resolve(null);
    });
    document.head.appendChild(script);
  });
  return placesLoaderPromise;
}

function AddressControl({
  value,
  onChange,
  placeholder,
  disabled,
  error,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  error?: boolean | undefined;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    let cancelled = false;
    void loadGooglePlaces().then((places) => {
      const input = inputRef.current;
      if (cancelled || !places || !input) return;
      const autocomplete = new places.Autocomplete(input, {
        types: ['address'],
        fields: ['formatted_address'],
      });
      autocomplete.addListener('place_changed', () => {
        const formatted = autocomplete.getPlace().formatted_address;
        if (formatted) onChangeRef.current(formatted);
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative">
      <Icon
        kind="pin"
        size={14}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)]"
      />
      <input
        ref={inputRef}
        value={asString(value)}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={placeholder ?? 'Start typing your address…'}
        disabled={disabled}
        autoComplete="off"
        className={cn(PF_FIELD_CLASS, 'pl-9', error && 'border-[var(--terracotta)]')}
      />
    </div>
  );
}

/** Map a resolved block/catalog field type to a concrete control. */
export function mapFieldControlType(blockOrFieldType: string): FieldControlType {
  switch (blockOrFieldType) {
    case 'textarea':
      return 'textarea';
    case 'number_input':
    case 'number':
    case 'patient_family_size':
      return 'number';
    case 'currency':
    case 'patient_income':
      return 'currency';
    case 'patient_email':
    case 'email':
      return 'email';
    case 'tel':
    case 'patient_mobile_phone':
    case 'patient_home_phone':
    case 'patient_work_phone':
      return 'tel';
    case 'date':
    case 'date_picker':
    case 'patient_dob':
      return 'date';
    case 'patient_ssn_last4':
      return 'ssn';
    case 'dropdown':
    case 'patient_title':
    case 'patient_suffix':
    case 'patient_gender':
    case 'patient_language':
    case 'patient_marital_status':
      return 'dropdown';
    case 'radio':
    case 'patient_contact_method':
      return 'radio';
    case 'checkbox':
      return 'checkbox';
    case 'checkbox_group':
      return 'checkbox_group';
    case 'search_multi':
    case 'patient_allergies':
    case 'patient_conditions':
    case 'patient_medications':
      return 'tags';
    case 'signature':
    case 'signature_pad':
      return 'signature';
    case 'file':
    case 'file_upload':
      return 'file';
    case 'rich_text':
    case 'rich_text_locked':
    case 'rich_text_display':
      return 'display';
    case 'dynamic_list':
      return 'dynamic_list';
    case 'google':
    case 'patient_address':
      return 'address';
    case 'search':
    case 'text':
    case 'text_input':
    default:
      return 'text';
  }
}

/** Normalize catalog/config options into a flat string list. */
export function normalizeOptions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const rec = item as { label?: unknown; value?: unknown };
        if (typeof rec.label === 'string') return rec.label;
        if (typeof rec.value === 'string') return rec.value;
      }
      return '';
    })
    .filter((s): s is string => s.length > 0);
}

export function catalogFieldLabel(field: ResolvedFormCatalogField): string {
  return field.label ?? field.id;
}
